import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Inventory, DataType, SearchState, SearchFlyoutProps, Errors } from './types';
import {
  Button,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  FlyoutContainer,
  Dropdown
} from '@cmhc-schl/hearth-ui';
import { Box, SelectChangeEvent, Typography, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getFields } from './utils/getFields';
import { getLabel } from './utils/getLabel';
import InputField from '../InputField';
import styles from './styles.module.scss';
import { validateFields } from './utils/validateFields';
import { getInventories } from './utils/getInventories';
import { getDataTypes } from './utils/getDataTypes';
import { useLocation, useHistory } from 'react-router-dom';
import { getSearchParams } from './utils/getSearchParams';
import { setSearchParams } from './utils/setSearchParams';

const SearchFlyout: React.FC<SearchFlyoutProps> = ({
  openSearchFlyout,
  toggleSearchDrawer,
  prefillFromLast
}) => {
  const { t } = useTranslation('SharedPortfolio');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const location = useLocation();
  const history = useHistory();
  const fieldRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const isItValidationPage = location.pathname.includes('/portfolio-management');

  const [form, setForm] = useState<SearchState>({
    inventory: isItValidationPage ? 'Validation' : 'Pool',
    dataType: undefined,
    query: {}
  });

  useEffect(() => {
    const { dataType, searchQuery } = getSearchParams(location.search);

    if (prefillFromLast && !!dataType && searchQuery) {
      setForm((prevState) => ({
        ...prevState,
        dataType: dataType,
        query: { ...searchQuery }
      }));
    } else {
      setForm((prevState) => ({
        ...prevState,
        inventory: isItValidationPage ? 'Validation' : 'Pool',
        dataType: undefined,
        query: {}
      }));
    }
  }, [prefillFromLast, getSearchParams]);

  const getDropdownOptions = (data: string[]) =>
    data.map((item: string) => ({ label: t(`${getLabel(item)}`), value: item })) || [];

  const dataTypes = useMemo(() => form.inventory && getDataTypes(form.inventory), [form.inventory]);

  const fields = useMemo(
    () => form.inventory && form.dataType && getFields(form.inventory, form.dataType),
    [form.inventory, form.dataType]
  );

  const getFieldInputRef = useCallback(
    (fieldName: string) => (element: HTMLInputElement | null) => {
      fieldRefs.current[fieldName] = element;
    },
    []
  );

  useEffect(() => {
    if (!Array.isArray(fields) || !fields.length) return;

    const firstFieldName = fields[0]?.name;
    if (!firstFieldName) return;

    const firstFieldElement = fieldRefs.current[firstFieldName];
    firstFieldElement?.focus();
  }, [fields]);

  const inventoryDropdownOptions = useMemo(
    () => getDropdownOptions(getInventories),
    [getInventories]
  );

  const dataTypesDropdownOptions = useMemo(
    () => (dataTypes ? getDropdownOptions(dataTypes) : []),
    [dataTypes]
  );

  const handleChangeDropdownValue = (type: string, e: SelectChangeEvent<string>) => {
    const selectedValue = e.target.value;
    if (type === 'inventory') {
      setForm(() => ({
        inventory: selectedValue as Inventory | undefined,
        dataType: undefined,
        query: {}
      }));
    }
    if (type === 'dataType') {
      setForm((prev) => ({
        ...prev,
        dataType: selectedValue as DataType | undefined,
        query: {}
      }));
    }
    setErrors({});
  };

  const handleFieldChange = (name: string, value: string, replaceAllSpace?: boolean) => {
    setForm((prev) => ({
      ...prev,
      query: { ...prev.query, [name]: replaceAllSpace ? value.replace(/\s+/g, '') : value }
    }));
  };

  const handleRedirect = () => {
    const shouldGoToSearchPage =
      form.dataType !== 'Application ID' &&
      form.dataType !== 'Portfolio ID' &&
      form.dataType !== 'Master Pool Number' &&
      form.dataType !== 'Sub Pool Number';

    const searchPage = shouldGoToSearchPage ? '/search' : '';

    const masterPoolPage = `/pages/master-pool${searchPage}`;
    const validationPage = `/pages/portfolio-management${searchPage}`;
    const searchParams = setSearchParams(form);

    if (location.pathname !== masterPoolPage && form.inventory === 'Pool') {
      history.push({
        pathname: masterPoolPage,
        search: searchParams ? `?${searchParams}` : ''
      });
    } else if (location.pathname !== validationPage && form.inventory === 'Validation') {
      history.push({
        pathname: validationPage,
        search: searchParams ? `?${searchParams}` : ''
      });
    } else {
      history.replace({
        pathname: location.pathname,
        search: searchParams ? `?${searchParams}` : ''
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => setSubmitting(false), 500);
    const isValid = validateFields({ form, fields, setErrors });

    if (!isValid) return;

    try {
      setSubmitting(true);
      handleRedirect();
    } catch (error: any) {
      throw new Error(`Error while submitting search query, ${error!!}`);
    } finally {
      setSubmitting(false);
      toggleSearchDrawer(false);
    }
  };

  const handleCancelFlyout = () => {
    setErrors({});
    setForm((prevState) => ({
      ...prevState,
      inventory: isItValidationPage ? 'Validation' : 'Pool',
      dataType: undefined,
      query: {}
    }));
    toggleSearchDrawer(false);
  };

  return (
    <FlyoutContainer
      id="search-flyout-container"
      isHeaderRequired={true}
      headerText={t('title')}
      open={openSearchFlyout}
      onClose={handleCancelFlyout}
      content={
        <form onSubmit={handleSubmit} className={styles.searchFlyoutForm}>
          <Box role="presentation" className={styles.searchFlyoutContainer}>
            <Box>
              <Typography>{t('inventory-title')}</Typography>
              <Dropdown
                placeholderText={t('select')}
                options={inventoryDropdownOptions}
                onChange={(e) => handleChangeDropdownValue('inventory', e)}
                initialOption={form.inventory ?? ''}
                errorMessage={t(`${errors.inventory}`)}
                error={!!errors.inventory}
                data-testid="inventory"
              />
            </Box>
            <Box>
              <Typography>{t('data-type-title')}</Typography>
              <Dropdown
                key={`${form.inventory}-${form?.dataType}`}
                placeholderText={t('select')}
                options={dataTypesDropdownOptions}
                onChange={(e) => handleChangeDropdownValue('dataType', e)}
                initialOption={form?.dataType ?? ''}
                error={!!errors.datatType}
                errorMessage={t(`${errors.datatType}`)}
                data-testid="data-type"
              />
            </Box>
            {fields && (
              <fieldset>
                <Box className={styles.fieldsWrapper}>
                  {fields?.map((field, index) => (
                    <Box className={styles.fieldWrapper} key={index}>
                      <InputField
                        id={t(`${getLabel(field.label)}`)}
                        label={t(`${getLabel(field.label)}`)}
                        value={form.query?.[field.name] ?? ''}
                        onChange={(value) =>
                          handleFieldChange(field.name, value, field.replaceAllSpace)
                        }
                        inputRef={getFieldInputRef(field.name)}
                        error={
                          errors.numericError
                            ? t('numeric-error')
                            : errors[field.name] && `${t(getLabel(field.label))} ${t('required')}`
                        }
                        leadingIcon={
                          <CmhcIcon
                            size={CmhcIconSize.SMALL}
                            type="icon_search"
                            className={styles.errorInfoIcon}
                          />
                        }
                        placeholder={t(`${getLabel(field.label)}`)}
                        errorStatus={!!errors.inlineError}
                      />
                    </Box>
                  ))}
                  {errors.inlineError && (
                    <div className={styles.fieldErrorContainer}>
                      <CmhcIcon
                        size={CmhcIconSize.SMALL}
                        type="icon_info_outlined"
                        className={styles.errorInfoIcon}
                      />
                      <span role="alert" className={styles.errorMessageText}>
                        {t('inline-error')}
                      </span>
                    </div>
                  )}
                </Box>
              </fieldset>
            )}
          </Box>
          <Box className={styles.flyoutFooter}>
            <Stack direction="row" spacing={3}>
              <Button
                data-test-id="cancel"
                type="button"
                name={'cancel'}
                onClick={handleCancelFlyout}
                className={styles.fullWidth}
                variant={ButtonVariant.SECONDARY}
                text={{
                  value: t('cancel')
                }}
                ariaText={{
                  value: t('cancel')
                }}
                role="button"
              />

              <Button
                data-test-id="show-result"
                type="submit"
                name={'show results'}
                className={styles.fullWidth}
                variant={ButtonVariant.PRIMARY}
                text={{
                  value: `${submitting ? 'Loading...' : t('show-results')}`
                }}
                ariaText={{
                  value: `${submitting ? 'Loading...' : t('show-results')}`
                }}
                role="button"
              />
            </Stack>
          </Box>
        </form>
      }
    />
  );
};

export default SearchFlyout;
